import { ArrowLeft } from 'lucide-react';
import { useIsDesktop } from '@/core/hooks/useMediaQuery';
import { Sheet, SheetContent, SheetTitle } from '@/shared/widgets/sheet';
import { Dialog, DialogContent, DialogTitle } from '@/shared/widgets/dialog';
import { usePopupHistory } from '@/shared/hooks/usePopupHistory';
import chainSizeImage from '@/shared/assets/images/chainsize.jpg';

interface ChainLengthGuideSheetProps {
  open: boolean;
  onClose: () => void;
}

function ChainLengthGuideContent({
  onClose,
  TitleComponent,
}: {
  onClose: () => void;
  TitleComponent: typeof SheetTitle | typeof DialogTitle;
}) {
  return (
    <>
      <div className="flex shrink-0 items-center border-b border-[#e2e2e2] bg-white px-1 py-3">
        <button
          type="button"
          aria-label="Close chain length guide"
          className="p-3 text-[var(--link-blue)]"
          onClick={onClose}
        >
          <ArrowLeft className="h-[22px] w-[22px]" />
        </button>
        <TitleComponent className="text-[18px] font-semibold leading-none text-black">
          Select Length (in inches)
        </TitleComponent>
      </div>

      <div className="flex-1 overflow-y-auto bg-white">
        <img src={chainSizeImage} alt="Chain length guide" className="w-full" />
      </div>

      <div className="shrink-0 border-t border-[#e2e2e2] p-4">
        <button
          type="button"
          className="w-full py-2 text-sm font-medium uppercase text-[var(--link-blue)]"
          onClick={onClose}
        >
          Got It
        </button>
      </div>
    </>
  );
}

export function ChainLengthGuideSheet({ open, onClose }: ChainLengthGuideSheetProps) {
  usePopupHistory({ isOpen: open, onClose, stateKey: 'chainLengthGuide' });
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
        <DialogContent
          showCloseButton={false}
          className="flex max-h-[85vh] w-[calc(100%-2rem)] max-w-[520px] flex-col gap-0 overflow-hidden p-0"
        >
          <ChainLengthGuideContent onClose={onClose} TitleComponent={DialogTitle} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className="flex max-h-[85dvh] flex-col gap-0 p-0"
      >
        <ChainLengthGuideContent onClose={onClose} TitleComponent={SheetTitle} />
      </SheetContent>
    </Sheet>
  );
}
